import { defineStore } from 'pinia'
import * as api from '@/api'
import { cacheGet, cacheSet } from '@/utils/cache'

// 生成匿名密钥（64 hex）
function genAnonKey() {
  const arr = new Uint8Array(32)
  crypto.getRandomValues(arr)
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')
}

export const useUserStore = defineStore('user', {
  state: () => ({
    token: localStorage.getItem('crirp_token') || '',
    // 首屏秒开：同步读本地缓存，登录态立即可用，后台 fetchMe 静默刷新
    user: cacheGet('user', null),
    unread: 0,
    dmUnread: 0,
    likedPostIds: JSON.parse(localStorage.getItem('crirp_liked') || '[]'),
    likedCommentIds: JSON.parse(localStorage.getItem('crirp_liked_comments') || '[]'),
    lastUserCenterFk: null,
  }),
  getters: {
    isLogin: (s) => !!s.token && !!s.user,
    hasEmail: (s) => s.user && s.user.type === 2,
    isAdmin: (s) => !!(s.user && s.user.is_admin),
  },
  actions: {
    setToken(token) {
      this.token = token
      if (token) localStorage.setItem('crirp_token', token)
      else localStorage.removeItem('crirp_token')
    },

    // 统一写入 user：内存 + 本地缓存，保证刷新后首屏登录态同步可用
    setUser(user) {
      this.user = user
      cacheSet('user', user)
    },

    // 匿名登录：本地持久化密钥
    async anonymousLogin(username) {
      let anonKey = localStorage.getItem('crirp_anon_key')
      if (!anonKey) {
        anonKey = genAnonKey()
        localStorage.setItem('crirp_anon_key', anonKey)
      }
      const res = await api.anonymousLogin(anonKey, username)
      this.setToken(res.data.token)
      this.setUser(res.data.user)
      return res
    },

    async emailLogin(email, code) {
      const anonKey = localStorage.getItem('crirp_anon_key') || ''
      const res = await api.emailLogin(email, code, anonKey)
      this.setToken(res.data.token)
      this.setUser(res.data.user)
      return res
    },

    async fetchMe() {
      if (!this.token) return null
      try {
        const res = await api.getMe()
        this.setUser(res.data)
        // liked 列表外移：本地维护并持久化
        this.likedPostIds = Array.isArray(res.data.liked_post_ids)
          ? res.data.liked_post_ids.map(Number)
          : []
        localStorage.setItem('crirp_liked', JSON.stringify(this.likedPostIds))
        // 评论点赞列表同理（评论列表走 CDN，不带用户态，is_liked 由前端按此列表判定）
        this.likedCommentIds = Array.isArray(res.data.liked_comment_ids)
          ? res.data.liked_comment_ids.map(Number)
          : []
        localStorage.setItem('crirp_liked_comments', JSON.stringify(this.likedCommentIds))
        return res.data
      } catch (e) {
        // 401 已由 http 全局 handler 处理；其他错误忽略，保留登录态
        return null
      }
    },

    isLiked(postId) {
      return this.likedPostIds.includes(Number(postId))
    },
    toggleLikedLocal(postId) {
      postId = Number(postId)
      const idx = this.likedPostIds.indexOf(postId)
      if (idx >= 0) this.likedPostIds.splice(idx, 1)
      else this.likedPostIds.push(postId)
      localStorage.setItem('crirp_liked', JSON.stringify(this.likedPostIds))
    },
    isLikedComment(commentId) {
      return this.likedCommentIds.includes(Number(commentId))
    },
    toggleLikedCommentLocal(commentId) {
      commentId = Number(commentId)
      const idx = this.likedCommentIds.indexOf(commentId)
      if (idx >= 0) this.likedCommentIds.splice(idx, 1)
      else this.likedCommentIds.push(commentId)
      localStorage.setItem('crirp_liked_comments', JSON.stringify(this.likedCommentIds))
    },
    // user_center freshKey 节流：fk 未变则不重拉 getMe（liked 列表）
    async maybeRefreshByFk() {
      if (!this.token || !this.user) return
      try {
        const fk = (await api.getFresh('user_center', this.user.id)).data.freshKey
        if (this.lastUserCenterFk === null) {
          this.lastUserCenterFk = fk   // 首次仅建立基线，避免与启动 fetchMe 重复
        } else if (fk !== this.lastUserCenterFk) {
          this.lastUserCenterFk = fk
          await this.fetchMe()
        }
      } catch (e) { /* ignore */ }
    },

    async fetchUnread() {
      if (!this.token) { this.unread = 0; return }
      try {
        const res = await api.getUnread()
        this.unread = res.data.unread || 0
      } catch (e) { /* ignore */ }
    },

    async fetchDmUnread() {
      if (!this.token) { this.dmUnread = 0; return }
      try {
        const res = await api.getDmUnread()
        this.dmUnread = res.data.unread || 0
      } catch (e) { /* ignore */ }
    },

    // 被动清登录态（接口 401）：纯本地清理，不发请求，避免递归
    clearSession() {
      this.setToken('')
      this.setUser(null)
      this.unread = 0
      this.dmUnread = 0
      this.likedPostIds = []
      this.likedCommentIds = []
      this.lastUserCenterFk = null
      localStorage.removeItem('crirp_liked')
      localStorage.removeItem('crirp_liked_comments')
    },
    // 主动登出：通知后端 revoke token，再清本地
    async logout() {
      try { await api.logout() } catch (e) { /* ignore */ }
      this.clearSession()
    },
  },
})
